import { Cultivar, Order } from '~/api-client/data-contracts'

type Included = {
  id: string
  type: string
  attributes: Record<string, any>
  relationships: Record<string, { data: { id: string; type: string } | { id: string; type: string }[] }>
}

type PlacementLike = {
  relationships?: {
    order?: { data?: { id: string; type: string } | null }
    schedule?: { data?: { id: string; type: string } | null }
  }
}

export type DeliveryAddress = {
  postalCode?: string
  address: string
  name?: string
}

export type DeliverySummary = {
  deliverAt?: number
  total: number
  imageUrl?: string
  description?: string
  address?: DeliveryAddress
  invoiceUrl?: string
  cultivars?: Cultivar[]
}

export function findShippingStatus(order: Order, included: Included[]) {
  if (!order) return null
  if (order.attributes.capture !== 'completed') return null

  const orderItem = included.find((item) => item.type === 'order_item' && order.relationships.order_items.data.some((oi) => oi.id === item.id))
  if (!orderItem) return null

  return included.find((item) => {
    return item.type === 'shipping_status'
      && Array.isArray(orderItem.relationships.shipping_statuses.data)
      && orderItem.relationships.shipping_statuses.data.some((ss) => ss.id === item.id)
  })
}

export function findOrder(placement: PlacementLike, included: Included[]) {
  const order = placement.relationships?.order?.data

  if (!order) return null

  return included.find((item) => item.type === 'order' && item.id === order.id)
}

export function findSchedule(placement: PlacementLike, included: Included[]) {
  const schedule = placement.relationships?.schedule?.data

  if (!schedule) return null

  return included.find((item) => item.type === 'subscription_schedule' && item.id === schedule.id)
}

export function findCultivars(schedule: Included | null | undefined, included: Included[]) {
  const cultivars = schedule?.relationships?.cultivars?.data

  if (!Array.isArray(cultivars)) return []

  return cultivars
    .map((cultivar) => included.find((item) => item.type === 'cultivar' && item.id === cultivar.id))
    .filter((cultivar): cultivar is Included => cultivar !== undefined)
}

export function findAddress(order: Included, included: Included[]) {
  const address = order.relationships?.address?.data

  if (!address || Array.isArray(address)) return null

  return included.find((item) => item.type === 'address' && item.id === address.id)
}

function toDeliveryAddress(address: Included | null | undefined): DeliveryAddress | undefined {
  if (!address) return undefined

  const postalCode: string | undefined = address.attributes.postal_code

  return {
    postalCode: postalCode?.replace(/(\d{3})(\d{4})/, '$1-$2'),
    address: [address.attributes.state, address.attributes.city_and_street, address.attributes.others].filter(Boolean).join(''),
    name: address.attributes.name,
  }
}

// 注文済みの予定から、お届け履歴のカードに出す値だけを取り出す
export function toDeliverySummary(placement: PlacementLike, included: Included[]): DeliverySummary | null {
  const order = findOrder(placement, included)

  if (!order) return null

  const schedule = findSchedule(placement, included)
  const shippingStatus = findShippingStatus(order as unknown as Order, included)
  const cultivars = findCultivars(schedule, included)

  return {
    deliverAt: shippingStatus?.attributes.deliver_at ?? schedule?.attributes.deliver_at?.[0],
    total: order.attributes.total,
    imageUrl: schedule?.attributes.image_url,
    description: schedule?.attributes.description,
    address: toDeliveryAddress(findAddress(order, included)),
    invoiceUrl: order.attributes.invoice_url ?? undefined,
    cultivars,
  }
}
